import type { WebWidgetModel } from '@web/types';

import { Injectable } from '@angular/core';
import { BehaviorSubject, type Observable } from 'rxjs';

import { BaseWidgetsService } from '@common/services/base-widgets.service';

import widgets from '@web/core/constants/widgets';

@Injectable({
    providedIn: 'root',
})
export class WebWidgetsService extends BaseWidgetsService<WebWidgetModel> {
    protected override widgets: (WebWidgetModel | any)[] = widgets;
    private specificWidgets$: BehaviorSubject<WebWidgetModel[]>;
    private zeroWidgets$: BehaviorSubject<WebWidgetModel[]>;

    /**
     * Zero-block variants that are built but not yet released to users. Empty
     * since login-edu — the last hold-out — was migrated to signal forms and
     * got its zero variant (extended-position grid + content-height sync).
     */
    private readonly notReleasedZeroWidgetsSet: Set<string> = new Set([]);

    protected init(): void {
        widgets.forEach((widget: WebWidgetModel) => this.widgetsMap.set(widget.name, widget));
    }

    public getVisibleWidgets$(): Observable<WebWidgetModel[]> {
        if (!this.visibleWidgets$) {
            this.visibleWidgets$ = new BehaviorSubject(
                this.filterHiddenWidgets().filter((widget) => widget.params.type.includes('default'))
            );
        }

        return this.visibleWidgets$;
    }

    public getSpecificWidgets$(): Observable<WebWidgetModel[]> {
        if (!this.specificWidgets$) {
            this.specificWidgets$ = new BehaviorSubject(
                this.filterHiddenWidgets().filter((widget) => widget.params.type.includes('specific'))
            );
        }

        return this.specificWidgets$;
    }

    public getZeroWidgets$(): Observable<WebWidgetModel[]> {
        if (!this.zeroWidgets$) {
            this.zeroWidgets$ = new BehaviorSubject(
                this.filterHiddenWidgets()
                    .filter((widget) => widget.params.type.includes('zero'))
                    .map((item) =>
                        this.notReleasedZeroWidgetsSet.has(item.name)
                            ? { ...item, permission: { isReleased: false } }
                            : item
                    )
            );
        }

        return this.zeroWidgets$;
    }
}
